import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, MapPin, TrendingUp, Calendar, DollarSign, FileText, MessageSquare, Target, Briefcase, AlertTriangle, Activity, Phone, Mail, Globe, User, Compass, ExternalLink, Minimize2, GripHorizontal } from 'lucide-react';
import { formatMetres, isRecent } from '../utils/formatters';
import { sanitize } from '../utils/sanitize';
import TalkingPoints from './TalkingPoints';
import StoreLifecycleActions from './StoreLifecycleActions';

/**
 * EntityDetailPanel - Persona-aware detail panel for Leads and Customers
 *
 * Transforms visual presentation based on entity_type:
 * - Lead: Blue/Purple gradient, Target icon, "Opportunity Value", AI insights
 * - Customer: Emerald gradient, Briefcase icon, "Account Revenue", Health metrics
 */
const LeadDetailPanel = ({ lead, onClose, onAddToVisitList, isInVisitList, panelMode = false, isFloating = false, onToggleFloat }) => {
    // Mobile bottom sheet: expanded vs collapsed (70vh default)
    const [sheetExpanded, setSheetExpanded] = useState(false);
    const sheetHandleRef = useRef(null);
    const sheetStartY = useRef(0);

    // Float mode: draggable position
    const [floatPos, setFloatPos] = useState(() => ({ x: Math.max(0, window.innerWidth - 430), y: 60 }));
    const floatDragOffset = useRef({ x: 0, y: 0 });
    const isFloatDragging = useRef(false);

    // Mobile bottom sheet touch drag
    useEffect(() => {
        const handle = sheetHandleRef.current;
        if (!handle || isFloating || panelMode) return;
        const onTouchStart = (e) => { sheetStartY.current = e.touches[0].clientY; };
        const onTouchEnd = (e) => {
            const dy = e.changedTouches[0].clientY - sheetStartY.current;
            if (dy < -50) setSheetExpanded(true);
            else if (dy > 80) onClose();
        };
        handle.addEventListener('touchstart', onTouchStart, { passive: true });
        handle.addEventListener('touchend', onTouchEnd, { passive: true });
        return () => {
            handle.removeEventListener('touchstart', onTouchStart);
            handle.removeEventListener('touchend', onTouchEnd);
        };
    }, [isFloating, panelMode, onClose]);

    // Float mode: drag via header (desktop)
    const startFloatDrag = useCallback((e) => {
        if (!isFloating) return;
        e.preventDefault();
        isFloatDragging.current = true;
        floatDragOffset.current = { x: e.clientX - floatPos.x, y: e.clientY - floatPos.y };
        const onMove = (e) => {
            if (!isFloatDragging.current) return;
            setFloatPos({ x: e.clientX - floatDragOffset.current.x, y: e.clientY - floatDragOffset.current.y });
        };
        const onUp = () => {
            isFloatDragging.current = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [isFloating, floatPos]);

    if (!lead) return null;

    const isCustomer = lead.entity_type === 'customer';
    const isLead = lead.entity_type === 'lead';

    // Persona-aware styling
    const headerGradient = isCustomer
        ? 'bg-gradient-to-r from-emerald-600 to-emerald-500'
        : 'bg-gradient-to-r from-blue-600 to-purple-600';

    const headerIconColor = isCustomer ? 'text-emerald-100' : 'text-blue-100';

    const HeaderIcon = isCustomer ? Briefcase : Target;

    // Compute container class
    let containerClass, containerStyle;
    if (isFloating) {
        containerClass = 'fixed z-[60] bg-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col';
        containerStyle = { left: floatPos.x, top: floatPos.y, width: 420, maxHeight: '85vh' };
    } else if (panelMode) {
        containerClass = 'h-full overflow-hidden flex flex-col bg-gray-800';
        containerStyle = {};
    } else {
        containerClass = sheetExpanded
            ? 'fixed inset-0 z-[55] bg-gray-800 overflow-hidden flex flex-col'
            : 'fixed inset-x-0 bottom-0 z-[55] bg-gray-800 rounded-t-2xl shadow-2xl overflow-hidden flex flex-col';
        containerStyle = sheetExpanded ? {} : { height: '70vh' };
    }

    return (
        <div data-testid="lead-detail-panel" className={containerClass} style={containerStyle}>
            {/* Mobile grab handle */}
            {!panelMode && !isFloating && (
                <div
                    ref={sheetHandleRef}
                    className="flex-shrink-0 flex justify-center items-center py-2 cursor-grab active:cursor-grabbing bg-gray-800"
                >
                    <div className="w-10 h-1 bg-gray-600 rounded-full" />
                </div>
            )}
            {/* Persona-Aware Header */}
            <div
                className={`flex-shrink-0 ${headerGradient} p-6 text-white sticky top-0 z-10 ${isFloating ? 'cursor-grab active:cursor-grabbing' : ''}`}
                onMouseDown={isFloating ? startFloatDrag : undefined}
            >
                {isFloating && (
                    <div className="flex justify-center mb-2">
                        <GripHorizontal className="w-4 h-4 text-white opacity-60" />
                    </div>
                )}
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <HeaderIcon className="w-6 h-6" />
                            <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
                                {isCustomer ? 'Customer Account' : 'Sales Prospect'}
                            </span>
                        </div>
                        <h2 className="text-2xl font-bold mb-1">
                            {lead.name || `${isCustomer ? 'Customer' : 'Lead'} #${lead.id}`}
                        </h2>
                        <div className={`flex items-center gap-2 ${headerIconColor} text-sm`}>
                            <MapPin className="w-4 h-4" />
                            <span>{lead.address || 'No address'}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                        {panelMode && onToggleFloat && (
                            <button aria-label="Float panel" onClick={onToggleFloat} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors" title="Float panel">
                                <ExternalLink className="w-4 h-4" />
                            </button>
                        )}
                        {isFloating && onToggleFloat && (
                            <button aria-label="Dock panel" onClick={onToggleFloat} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors" title="Dock panel">
                                <Minimize2 className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            data-testid="lead-detail-close"
                            aria-label="Close detail panel"
                            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto scroll-pane p-6 space-y-6 pb-24 md:pb-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-900/30 p-4 rounded-lg border border-emerald-800/50">
                        <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold mb-1">
                            <TrendingUp className="w-4 h-4" />
                            STATUS
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-bold inline-block ${
                            lead.status === 'new' ? 'bg-blue-500 text-white' :
                            lead.status === 'planned' ? 'bg-purple-600 text-white' :
                            lead.status === 'contacted' ? 'bg-sky-400 text-white' :
                            lead.status === 'won' ? 'bg-green-600 text-white' :
                            lead.status === 'lost' ? 'bg-gray-400 text-white' :
                            lead.status === 'active' ? 'bg-emerald-500 text-white' :
                            lead.status === 'at_risk' ? 'bg-orange-500 text-white' :
                            lead.status === 'inactive' ? 'bg-gray-400 text-white' :
                            lead.status === 'churned' ? 'bg-red-500 text-white' :
                            'bg-blue-500 text-white'
                            }`}>
                            {(lead.status || 'new').toUpperCase().replace('_', ' ')}
                        </div>
                    </div>

                    <div className="bg-orange-900/30 p-4 rounded-lg border border-orange-800/50">
                        <div className="flex items-center gap-2 text-orange-400 text-xs font-semibold mb-1">
                            <Calendar className="w-4 h-4" />
                            {isCustomer ? 'TIER' : 'PRIORITY'}
                        </div>
                        <div className="text-lg font-bold text-orange-200">
                            {isCustomer && lead.tier ? (
                                `⭐ Tier ${lead.tier}`
                            ) : (
                                lead.priority === 'high' ? '🔥🔥🔥 High' :
                                    lead.priority === 'medium' ? '⚡⚡ Medium' :
                                        '❄️ Low'
                            )}
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                {(lead.phone || lead.email || lead.website || lead.contact_person) && (
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                        <div className="flex items-center gap-2 text-gray-200 font-semibold mb-3">
                            <User className="w-5 h-5" />
                            Contact Information
                        </div>
                        <div className="space-y-2">
                            {lead.contact_person && (
                                <div className="flex items-center gap-3">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-200 font-medium">{lead.contact_person}</span>
                                </div>
                            )}
                            {lead.phone && (
                                <a
                                    href={`tel:${lead.phone}`}
                                    className="flex items-center gap-3 text-blue-400 hover:text-blue-300 hover:bg-gray-700 p-2 rounded-lg transition-colors"
                                >
                                    <Phone className="w-4 h-4" />
                                    <span className="text-sm font-medium">{lead.phone}</span>
                                </a>
                            )}
                            {lead.email && (
                                <a
                                    href={`mailto:${lead.email}`}
                                    className="flex items-center gap-3 text-blue-400 hover:text-blue-300 hover:bg-gray-700 p-2 rounded-lg transition-colors"
                                >
                                    <Mail className="w-4 h-4" />
                                    <span className="text-sm font-medium">{lead.email}</span>
                                </a>
                            )}
                            {lead.website && (
                                <a
                                    href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 text-blue-400 hover:text-blue-300 hover:bg-gray-700 p-2 rounded-lg transition-colors"
                                >
                                    <Globe className="w-4 h-4" />
                                    <span className="text-sm font-medium break-all">{lead.website}</span>
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {/* Persona-Aware Financial Section */}
                {(lead.actual_sales || lead.forecasted_sales || lead.monthly_recurring_revenue) && (
                    <div className={`rounded-lg p-4 border ${isCustomer
                        ? 'bg-emerald-900/30 border-emerald-800/50'
                        : 'bg-blue-900/30 border-blue-800/50'
                        }`}>
                        <div className={`flex items-center gap-2 font-semibold mb-3 ${isCustomer ? 'text-emerald-400' : 'text-blue-400'
                            }`}>
                            <DollarSign className="w-5 h-5" />
                            {isCustomer ? 'Account Revenue' : 'Opportunity Value'}
                        </div>
                        <div className="space-y-2">
                            {isCustomer && lead.actual_sales !== undefined && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Total Sales:</span>
                                    <span className="font-bold text-emerald-300">¥{lead.actual_sales.toLocaleString()}</span>
                                </div>
                            )}
                            {isCustomer && lead.monthly_recurring_revenue && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Monthly Recurring:</span>
                                    <span className="font-bold text-emerald-300">¥{lead.monthly_recurring_revenue.toLocaleString()}/mo</span>
                                </div>
                            )}
                            {isLead && lead.forecasted_sales && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Forecasted Value:</span>
                                    <span className="font-bold text-blue-300">¥{lead.forecasted_sales.toLocaleString()}</span>
                                </div>
                            )}
                            {isLead && lead.opportunity_score && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Opportunity Score:</span>
                                    <span className="font-bold text-blue-300">{lead.opportunity_score.toFixed(1)}%</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* AI Recommendation Badge */}
                {lead.recommendation_score != null && lead.recommendation_score > 0 && (
                    <div className="bg-indigo-900/30 rounded-lg p-4 border border-indigo-700/50">
                        <div className="flex items-center gap-3">
                            <div className={`rounded-full p-2 ${lead.recommendation_score >= 0.8
                                ? 'bg-amber-800/50'
                                : 'bg-indigo-800/50'
                            }`}>
                                <Compass className={`w-5 h-5 ${lead.recommendation_score >= 0.8
                                    ? 'text-amber-400'
                                    : 'text-indigo-400'
                                }`} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-indigo-200">
                                        AI Score: {Math.round(lead.recommendation_score * 100)}%
                                    </span>
                                    {lead.recommendation_score >= 0.8 && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-800/60 text-amber-300 border border-amber-600/50">
                                            Top Pick
                                        </span>
                                    )}
                                </div>
                                {lead.recommendation_reason && (
                                    <p className="text-xs text-indigo-300 mt-1">{sanitize(lead.recommendation_reason)}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Recent Transactions List - For Customers Only */}
                {isCustomer && lead.sales_transactions && lead.sales_transactions.length > 0 && (
                    <div className="bg-gray-700/50 rounded-lg p-4 border border-emerald-800/40">
                        <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-3">
                            <TrendingUp className="w-5 h-5" />
                            Recent Transactions
                        </div>
                        <div className="space-y-3">
                            {lead.sales_transactions.slice(0, 5).map((txn) => (
                                <div key={txn.id} className="bg-emerald-900/30 rounded p-3 border border-emerald-800/40">
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="text-sm font-semibold text-emerald-200">
                                            {txn.product_name || 'General Product'}
                                        </div>
                                        <div className="text-sm font-bold text-emerald-400">
                                            ¥{txn.amount.toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center text-xs text-emerald-500">
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(txn.sales_date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </div>
                                        {txn.quantity > 1 && (
                                            <div className="bg-emerald-800/50 px-1.5 rounded text-emerald-300">Qty: {txn.quantity}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {lead.sales_transactions.length > 5 && (
                                <div className="text-center pt-2">
                                    <button className="text-xs text-emerald-400 font-semibold hover:text-emerald-300">
                                        View all {lead.sales_transactions.length} transactions
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Customer Health Section - Only for Customers */}
                {isCustomer && (lead.churn_risk_score || lead.churn_risk_reasons || lead.visit_frequency) && (
                    <div className="bg-orange-900/20 rounded-lg p-4 border border-orange-800/50">
                        <div className="flex items-center gap-2 text-orange-400 font-semibold mb-3">
                            <AlertTriangle className="w-5 h-5" />
                            Account Health
                        </div>
                        <div className="space-y-3">
                            {lead.churn_risk_score !== null && lead.churn_risk_score !== undefined && (
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm text-gray-400">Churn Risk:</span>
                                        <span className={`font-bold ${lead.churn_risk_score > 70 ? 'text-red-400' :
                                            lead.churn_risk_score > 40 ? 'text-orange-400' :
                                                'text-green-400'
                                            }`}>
                                            {lead.churn_risk_score.toFixed(1)}%
                                        </span>
                                    </div>
                                    {/* Track darkened; bar color preserved per spec */}
                                    <div className="w-full bg-gray-700 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full ${lead.churn_risk_score > 70 ? 'bg-red-600' :
                                                lead.churn_risk_score > 40 ? 'bg-orange-600' :
                                                    'bg-green-600'
                                                }`}
                                            style={{ width: `${lead.churn_risk_score}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                            {lead.churn_risk_reasons && (
                                <div className="bg-gray-700/60 p-3 rounded border border-orange-700/50">
                                    <div className="text-xs font-semibold text-orange-400 mb-1">RISK FACTORS</div>
                                    <p className="text-sm text-gray-300">{sanitize(lead.churn_risk_reasons)}</p>
                                </div>
                            )}
                            {lead.visit_frequency && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Visit Frequency:</span>
                                    <span className="font-bold text-gray-200">{lead.visit_frequency} visits/month</span>
                                </div>
                            )}
                            {lead.last_visit_date && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Last Visit:</span>
                                    <span className="font-medium text-gray-200">
                                        {new Date(lead.last_visit_date).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}
                            {lead.last_order_date && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Last Order:</span>
                                    <span className="font-medium text-gray-200">
                                        {new Date(lead.last_order_date).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Type/Category & Industry */}
                {(lead.type || lead.industry) && (
                    <div className={`rounded-lg p-4 border ${isCustomer
                        ? 'bg-emerald-900/30 border-emerald-800/50'
                        : 'bg-blue-900/30 border-blue-800/50'
                        }`}>
                        {lead.type && (
                            <div className="mb-2">
                                <div className={`text-xs font-semibold mb-1 ${isCustomer ? 'text-emerald-400' : 'text-blue-400'
                                    }`}>
                                    BUSINESS TYPE
                                </div>
                                <div className={`text-sm font-medium ${isCustomer ? 'text-emerald-200' : 'text-blue-200'
                                    }`}>
                                    {lead.type}
                                </div>
                            </div>
                        )}
                        {lead.industry && (
                            <div>
                                <div className={`text-xs font-semibold mb-1 ${isCustomer ? 'text-emerald-400' : 'text-blue-400'
                                    }`}>
                                    INDUSTRY
                                </div>
                                <div className={`text-sm font-medium ${isCustomer ? 'text-emerald-200' : 'text-blue-200'
                                    }`}>
                                    {lead.industry}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Location Intelligence */}
                {(lead.city || lead.prefecture || lead.is_near_station !== null || lead.traffic_score) && (
                    <div className="bg-indigo-900/20 rounded-lg p-4 border border-indigo-700/40">
                        <div className="flex items-center gap-2 text-indigo-300 font-semibold mb-3">
                            <MapPin className="w-5 h-5" />
                            Location Intelligence
                        </div>
                        <div className="space-y-2">
                            {(lead.city || lead.prefecture) && (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 font-semibold uppercase">Location:</span>
                                    <span className="text-sm text-gray-200 font-medium">
                                        {[lead.city, lead.prefecture].filter(Boolean).join(', ')}
                                    </span>
                                </div>
                            )}
                            {lead.is_near_station !== null && lead.is_near_station && (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded-full font-semibold">
                                        🚉 Near Station
                                    </span>
                                    {lead.distance_from_station && (
                                        <span className="text-xs text-gray-400">
                                            ({formatMetres(lead.distance_from_station)} away)
                                        </span>
                                    )}
                                </div>
                            )}
                            {lead.traffic_score && (
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-400">Foot Traffic Score:</span>
                                    <span className={`font-bold ${lead.traffic_score > 80 ? 'text-green-400' :
                                        lead.traffic_score > 60 ? 'text-blue-400' :
                                            lead.traffic_score > 40 ? 'text-orange-400' :
                                                'text-gray-400'
                                        }`}>
                                        {lead.traffic_score}/100
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* AI Insights Section - For Both Leads and Customers */}
                {(lead.summary || lead.talking_points || lead.agent_rationale) && (
                    <div className={`rounded-lg p-4 border ${isCustomer
                        ? 'bg-teal-900/20 border-teal-700/40'
                        : 'bg-purple-900/20 border-purple-700/40'
                        }`}>
                        <div className={`flex items-center gap-2 font-semibold mb-3 ${isCustomer ? 'text-teal-300' : 'text-purple-300'
                            }`}>
                            <Activity className="w-5 h-5" />
                            AI Insights
                        </div>

                        {lead.agent_rationale && (
                            <div className="mb-3">
                                <div className={`flex items-center gap-2 font-medium mb-2 text-sm ${isCustomer ? 'text-teal-400' : 'text-purple-400'
                                    }`}>
                                    <Activity className="w-4 h-4" />
                                    {isCustomer ? 'Why This Account?' : 'Why This Lead?'}
                                </div>
                                <p className={`text-sm text-gray-300 leading-relaxed p-3 rounded border ${isCustomer
                                    ? 'bg-teal-900/30 border-teal-700/50'
                                    : 'bg-purple-900/30 border-purple-700/50'
                                    }`}>
                                    {sanitize(lead.agent_rationale)}
                                </p>
                            </div>
                        )}

                        {lead.summary && (
                            <div className="mb-3">
                                <div className={`flex items-center gap-2 font-medium mb-2 text-sm ${isCustomer ? 'text-teal-400' : 'text-gray-400'
                                    }`}>
                                    <FileText className="w-4 h-4" />
                                    Summary
                                </div>
                                <p className={`text-sm text-gray-300 leading-relaxed bg-gray-700/50 p-3 rounded border ${isCustomer ? 'border-teal-700/50' : 'border-purple-700/50'
                                    }`}>
                                    {sanitize(lead.summary)}
                                </p>
                            </div>
                        )}

                        {lead.talking_points && (
                            <div>
                                <div className={`flex items-center gap-2 font-medium mb-2 text-sm ${isCustomer ? 'text-teal-400' : 'text-gray-400'
                                    }`}>
                                    <MessageSquare className="w-4 h-4" />
                                    Talking Points
                                </div>
                                <div className="bg-amber-900/20 p-3 rounded border border-amber-700/40">
                                    <TalkingPoints text={lead.talking_points} accent="amber" panelMode={true} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Location Intelligence (GeoKG) */}
                {lead.area_intelligence && lead.area_intelligence.raw_available && (
                    lead.area_intelligence.archetype_name || lead.area_intelligence.opportunity_score != null
                ) && (
                    <div className="rounded-lg p-4 border bg-indigo-900/20 border-indigo-700/40">
                        <div className="flex items-center gap-2 font-semibold mb-3 text-indigo-300">
                            <Compass className="w-5 h-5" />
                            Location Intelligence
                        </div>

                        {/* Archetype */}
                        {lead.area_intelligence.archetype_name && (
                            <div className="mb-3">
                                <span className="text-xs text-indigo-400 uppercase tracking-wide font-medium">Area Type</span>
                                <p className="text-sm text-gray-200 mt-1 font-medium">{lead.area_intelligence.archetype_name}</p>
                                {lead.area_intelligence.archetype_description && (
                                    <p className="text-xs text-gray-400 mt-0.5">{lead.area_intelligence.archetype_description}</p>
                                )}
                            </div>
                        )}

                        {/* Score pills */}
                        {(lead.area_intelligence.opportunity_score != null ||
                          lead.area_intelligence.competition_saturation != null ||
                          lead.area_intelligence.business_density_score != null) && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {lead.area_intelligence.opportunity_score != null && (
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                                        lead.area_intelligence.opportunity_score >= 70
                                            ? 'bg-green-900/40 border-green-600/50 text-green-300'
                                            : lead.area_intelligence.opportunity_score >= 40
                                            ? 'bg-yellow-900/40 border-yellow-600/50 text-yellow-300'
                                            : 'bg-gray-700/50 border-gray-600/50 text-gray-400'
                                    }`}>
                                        <TrendingUp className="w-3 h-3" />
                                        Opportunity {Math.round(lead.area_intelligence.opportunity_score)}/100
                                    </div>
                                )}
                                {lead.area_intelligence.competition_saturation != null && (
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                                        lead.area_intelligence.competition_saturation <= 20
                                            ? 'bg-green-900/40 border-green-600/50 text-green-300'
                                            : lead.area_intelligence.competition_saturation <= 50
                                            ? 'bg-yellow-900/40 border-yellow-600/50 text-yellow-300'
                                            : 'bg-red-900/40 border-red-600/50 text-red-300'
                                    }`}>
                                        <Target className="w-3 h-3" />
                                        Competition {Math.round(lead.area_intelligence.competition_saturation)}%
                                    </div>
                                )}
                                {lead.area_intelligence.business_density_score != null && (
                                    <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-indigo-900/40 border-indigo-600/50 text-indigo-300">
                                        <MapPin className="w-3 h-3" />
                                        Density {Math.round(lead.area_intelligence.business_density_score)}/100
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Underserved signal */}
                        {lead.area_intelligence.is_underserved && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-900/30 border border-green-600/40 text-green-300 text-xs font-medium">
                                <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{lead.industry || 'This industry'} is underserved in this area — fewer competitors than average</span>
                            </div>
                        )}

                        {/* Recommended products — what customers like this one buy */}
                        {lead.area_intelligence.recommended_products?.length > 0 && (
                            <div className="mt-2 px-3 py-2 rounded-lg bg-blue-900/30 border border-blue-600/40">
                                <div className="text-xs font-semibold text-blue-300 mb-1.5">
                                    {lead.area_intelligence.recommendation_basis === 'nearby'
                                        ? 'Customers like this nearby buy'
                                        : 'Businesses of this type buy'}
                                </div>
                                <div className="space-y-1">
                                    {lead.area_intelligence.recommended_products.slice(0, 3).map((rec, i) => (
                                        <div key={i} className="flex items-center justify-between text-xs text-blue-100">
                                            <span className="font-medium">{rec.product_name}</span>
                                            <span className="text-blue-300/80">
                                                ¥{Math.round(rec.revenue || 0).toLocaleString()} · {rec.buyers} buyer{rec.buyers !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Communication History Timeline */}
                <div>
                    <div className="flex items-center gap-2 text-gray-300 font-semibold mb-3">
                        <Calendar className="w-5 h-5" />
                        {isCustomer ? 'Account Activity' : 'Communication History'}
                    </div>

                    {lead.history && lead.history.length > 0 ? (
                        <div className="relative border-l-2 border-gray-600 pl-6 space-y-4">
                            {lead.history.map((interaction, index) => {
                                // Format timestamp
                                const timestamp = interaction.timestamp
                                    ? new Date(interaction.timestamp).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })
                                    : 'Date unknown';

                                // Generate unique key combining multiple fields to avoid duplicates
                                const uniqueKey = interaction.id
                                    ? `interaction-${interaction.id}`
                                    : `${interaction.type || 'activity'}-${interaction.timestamp || index}-${index}`;

                                return (
                                    <div key={uniqueKey} className="relative">
                                        {/* Timeline dot */}
                                        <div className={`absolute -left-[1.6rem] top-1 w-4 h-4 rounded-full border-2 border-gray-800 ${isCustomer ? 'bg-emerald-600' : 'bg-blue-600'
                                            }`}></div>

                                        {/* Interaction card */}
                                        <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-semibold text-gray-100 text-sm">
                                                            {interaction.user_name || interaction.by || 'Sales Rep'}
                                                        </div>
                                                        {/* Recent indicator badge */}
                                                        {isRecent(interaction.timestamp, 7) && (
                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-800/60 text-yellow-300 border border-yellow-600/50">
                                                                RECENT
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{timestamp}</div>
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                    interaction.type === 'visit' ? 'bg-emerald-900/60 text-emerald-300' :
                                                        interaction.type === 'call' ? 'bg-blue-900/60 text-blue-300' :
                                                            interaction.type === 'email' ? 'bg-purple-900/60 text-purple-300' :
                                                                interaction.type === 'status_change' ? 'bg-amber-900/60 text-amber-300' :
                                                                    'bg-gray-600 text-gray-300'
                                                    }`}>
                                                    {interaction.type === 'visit' ? '📍 Visit' :
                                                        interaction.type || 'Contact'}
                                                </span>
                                            </div>
                                            {/* Lifecycle tags (+ voice sentiment) for consolidated visit cards */}
                                            {(interaction.tags && interaction.tags.length > 0) || interaction.sentiment ? (
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {(interaction.tags || []).map((tag, i) => (
                                                        <span key={i} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                            tag === 'checked_in' ? 'bg-blue-900/60 text-blue-300' :
                                                            tag === 'completed' ? 'bg-green-900/60 text-green-300' :
                                                            tag === 'skipped' ? 'bg-gray-600 text-gray-400' :
                                                            tag === 'cancelled' ? 'bg-red-900/60 text-red-300' :
                                                            'bg-gray-600 text-gray-300'
                                                        }`}>
                                                            {tag === 'checked_in' ? '📍 Checked In' :
                                                             tag === 'completed' ? '✅ Completed' :
                                                             tag === 'skipped' ? '⊘ Skipped' :
                                                             tag === 'cancelled' ? '✕ Cancelled' :
                                                             tag}
                                                        </span>
                                                    ))}
                                                    {/* Voice-note sentiment, shown in the same consolidated card */}
                                                    {interaction.sentiment && (
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                            interaction.sentiment === 'positive' ? 'bg-green-900/60 text-green-300' :
                                                            interaction.sentiment === 'negative' ? 'bg-red-900/60 text-red-300' :
                                                            'bg-gray-600 text-gray-300'
                                                        }`}>
                                                            {interaction.sentiment === 'positive' ? '🎤 😊 Positive' :
                                                             interaction.sentiment === 'negative' ? '🎤 😞 Negative' :
                                                             interaction.sentiment === 'neutral' ? '🎤 😐 Neutral' :
                                                             `🎤 ${interaction.sentiment}`}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : interaction.outcome && (
                                                <div className="text-xs mb-1">
                                                    <span className={`px-2 py-1 rounded-full font-semibold ${
                                                        interaction.outcome === 'positive' ? 'bg-green-900/60 text-green-300' :
                                                        interaction.outcome === 'negative' ? 'bg-red-900/60 text-red-300' :
                                                        interaction.outcome === 'neutral' ? 'bg-gray-600 text-gray-300' :
                                                        interaction.outcome === 'no_answer' ? 'bg-yellow-900/60 text-yellow-300' :
                                                        'bg-gray-600 text-gray-300'
                                                    }`}>
                                                        {interaction.outcome === 'positive' ? '😊 Positive' :
                                                         interaction.outcome === 'negative' ? '😞 Negative' :
                                                         interaction.outcome === 'neutral' ? '😐 Neutral' :
                                                         interaction.outcome === 'no_answer' ? '📵 No Answer' :
                                                         interaction.outcome}
                                                    </span>
                                                </div>
                                            )}
                                            {interaction.duration_minutes && (
                                                <div className="text-xs text-gray-500 mb-1">
                                                    Duration: {interaction.duration_minutes} minutes
                                                </div>
                                            )}
                                            <p className="text-sm text-gray-300 leading-relaxed">{sanitize(interaction.notes) || 'No notes available'}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-gray-700/30 rounded-lg p-6 border border-gray-700 text-center">
                            <div className="text-gray-600 mb-2">
                                <Calendar className="w-8 h-8 mx-auto" />
                            </div>
                            <p className="text-sm text-gray-500">
                                {isCustomer ? 'No account activity yet' : 'No communication history yet'}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                                {isCustomer ? 'First interaction will appear here' : 'First contact will appear here'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Action Button */}
                <button
                    onClick={() => onAddToVisitList(lead)}
                    disabled={isInVisitList(lead.global_id)}
                    data-testid="add-to-visit-list-btn"
                    data-in-list={isInVisitList(lead.global_id) ? 'true' : 'false'}
                    className={`w-full px-6 py-4 rounded-xl font-semibold transition-all text-sm flex items-center justify-center gap-2 ${isInVisitList(lead.global_id)
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : isCustomer
                            ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                            : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                        }`}
                >
                    {isInVisitList(lead.global_id) ? '✅ Added to Visit List' : '➕ Add to Visit List'}
                </button>

                {/* Store lifecycle (rule R6): report it closed or moved */}
                <StoreLifecycleActions entity={lead} onClosed={onClose} />
            </div>
        </div>
    );
};

export default LeadDetailPanel;
